import { useEffect, useState } from "react";
import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Paper from "@mui/material/Paper";
import dayjs from "dayjs";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionTitle from "../../components/AccordionTitle";

import {
  StripedTableCell,
  StripedTableRow,
} from "../../components/StripedTable";

import InputLabel from "@mui/material/InputLabel";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";

import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import { getHistoryForStudent } from "../../services/ActivityService";
import { formatDayJsDate } from "../../services/ServiceHelper";
import { hasError, getError } from "../../services/ServiceHelper";
import isEmpty from "lodash-es/isEmpty";

const ActivityHistory = () => {
  const [school] = useLocalStorage("Auth", "school", null);
  const [jwt] = useJwtStorage();

  const [showProgress, setShowProgress] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const [_dateStart, setDateStart] = useState(dayjs());
  const [_dateEnd, setDateEnd] = useState(dayjs().add(14, "day"));

  const [activities, setActivities] = useState([]);
  const [_periodLabels, setPeriodLabels] = useState([]);

  useEffect(() => {}, []);

  const handleSearch = () => {
    setSearchPerformed(true);

    setShowProgress(true);
    getHistoryForStudent(
      school,
      jwt,
      formatDayJsDate(_dateStart),
      formatDayJsDate(_dateEnd),
    )
      .then((results) => {
        if (hasError(results) === true) {
          window.showSnack(
            "Unable to get Activity History: " + getError(results),
          );
          return;
        }

        processActivities(results.data.activityDays);
        setPeriodLabels(results.data.periodLabels);
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  /**
   * input: map (key: Monday, Tuesday..., value: array of activities)
   * this unwraps the map and sorts the activities by date/period
   * @param mwtrf
   */
  const processActivities = (mtwrf) => {
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    let allActivities = [];

    // merge arrays together into single list
    for (let i = 0; i < daysOfWeek.length; i++) {
      const day = daysOfWeek[i];

      // get activities for day and merge into large array
      const dayActivities = mtwrf[day];

      if (dayActivities != null && dayActivities.length > 0) {
        allActivities = allActivities.concat(dayActivities);
      }
    }

    // sort curated list by date/period in each object
    let sortedActivities = allActivities.sort((a1, a2) => {
      const akey1 = a1.date + "-" + a1.period;
      const akey2 = a2.date + "-" + a2.period;

      if (akey1 > akey2) {
        return 1;
      }

      if (akey1 < akey2) {
        return -1;
      }

      return 0;
    });

    setActivities(sortedActivities);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Activity History" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Accordion
          disableGutters
          sx={{ boxShadow: 1, p: 0, mt: 2 }}
          defaultExpanded
        >
          <AccordionSummary className="accordionHeader">
            <AccordionTitle text={`Search Criteria`} />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InputLabel>Start Date</InputLabel>
                <MobileDatePicker
                  value={_dateStart}
                  onChange={(newValue) => {
                    setDateStart(newValue);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel>End Date</InputLabel>
                <MobileDatePicker
                  value={_dateEnd}
                  onChange={(newValue) => {
                    setDateEnd(newValue);
                  }}
                />
              </Grid>
            </Grid>
            <Button onClick={handleSearch} sx={{ mt: 2 }} variant="contained">
              Search
            </Button>
          </AccordionDetails>
        </Accordion>

        {searchPerformed === true && (
          <Accordion
            disableGutters
            sx={{ boxShadow: 1, p: 0, mt: 2 }}
            defaultExpanded
          >
            <AccordionSummary className="accordionHeader">
              <AccordionTitle text={`Search Results (${activities.length})`} />
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table
                  sx={{ minWidth: 340 }}
                  aria-label="future activities table"
                >
                  <TableBody>
                    {activities.map((a) => (
                      <>
                        <StripedTableRow key={"D" + a.id}>
                          <StripedTableCell>
                            <strong>
                              {dayjs(a.date).format("MM/DD")} -{" "}
                              {_periodLabels[a.period - 1].name}
                            </strong>
                          </StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow key={"DA" + a.id}>
                          <StripedTableCell>
                            <Grid container spacing={0}>
                              <Grid item xs={4} sm={2}>
                                <b>NAME:</b>
                              </Grid>
                              <Grid item xs={8} sm={10}>
                                {a.name}
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <b>LOCATION:</b>
                              </Grid>
                              <Grid item xs={8} sm={10}>
                                {a.locationName}
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <b>ADDED TO CALENDAR:</b>
                              </Grid>
                              <Grid item xs={8} sm={10}>
                                {dayjs(a.dateCreated).format("MM/DD/YYYY hh:mm A")}
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <b>PROCTOR:</b>
                              </Grid>
                              <Grid item xs={8} sm={10}>
                                {a.proctorLastName},{" "}
                                {a.proctorFirstName.charAt(0)}.
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <b>STUDENT CAN MODIFY:</b>
                              </Grid>
                              <Grid item xs={8} sm={10}>
                                {a.modify === 1 ? "YES" : "NO"}
                              </Grid>
                              {!isEmpty(a.comment) && (
                                <>
                                  <>
                                    <Grid item xs={4} sm={2}>
                                      <b>COMMENT:</b>
                                    </Grid>
                                    <Grid item xs={8} sm={10}>
                                      {a.comment}
                                    </Grid>
                                  </>
                                </>
                              )}
                              {!isEmpty(a.teacherComment) && (
                                <>
                                  <>
                                    <Grid item xs={4} sm={2}>
                                      <b>TEACHER COMMENT:</b>
                                    </Grid>
                                    <Grid item xs={8} sm={10}>
                                      {a.teacherComment}
                                    </Grid>
                                  </>
                                </>
                              )}
                              {a.assignedByLastName != null && (
                                <>
                                  <Grid item xs={4} sm={2}>
                                    <b>ASSIGNED BY:</b>
                                  </Grid>
                                  <Grid item xs={8} sm={10}>
                                    {a.assignedByLastName},{" "}
                                    {a.assignedByFirstName.charAt(0)}.
                                  </Grid>
                                </>
                              )}
                              {a.lockedByLastName != null && (
                                <>
                                  <Grid item xs={4} sm={2}>
                                    <b>LOCKED BY:</b>
                                  </Grid>
                                  <Grid item xs={8} sm={10}>
                                    {a.lockedByLastName},{" "}
                                    {a.lockedByFirstName.charAt(0)}.
                                  </Grid>
                                </>
                              )}
                            </Grid>
                          </StripedTableCell>
                        </StripedTableRow>
                      </>
                    ))}
                    {activities.length === 0 && (
                      <StripedTableRow>
                        <StripedTableCell colSpan={5}>
                          -- NO ACTIVITIES RETURNED --
                        </StripedTableCell>
                      </StripedTableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}
        {searchPerformed === false && (
          <Container sx={{ mt: 2, pl: 0, textAlign: "center" }}>
            -- CLICK SEARCH FOR RESULTS --
          </Container>
        )}
      </Container>
    </>
  );
};

export default ActivityHistory;
